import { errorMessage } from "@atlas/shared";
import React from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  Calendar,
  Check,
  Gift,
  Heart,
  MessageCircle,
  Send,
  Trash2,
  TriangleAlert,
  UsersRound,
  X,
} from "lucide-react";
import {
  addComment,
  deleteComment,
  deletePost,
  fetchComments,
  fetchPostLikers,
  formatRelativeTime,
  likePost,
  reportPost,
  unlikePost,
  type PostComment,
  type SocialPost,
} from "../api/social";
import { iconByName } from "../lib/achievement-icons";
import { useAuth } from "../hooks/useAuth";
import { getBannerAsset } from "../lib/cosmetics";
import { AtlasPanel } from "./AtlasPanel";
import { Button } from "./Button";
import { ProfileIdentity } from "./ProfileIdentity";
import { Skel } from "./Skeleton";
import { ActivityTraceSVG } from "./ActivityTraceSVG";
import { AwardPicker } from "./AwardPicker";
import { TIER } from "./AchievementBadge";

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

// ─── Embeds ─────────────────────────────────────────────────────────────────────

function AchievementEmbed({ achievement }: { achievement: NonNullable<SocialPost["achievement"]> }) {
  const t = TIER[achievement.tier] ?? TIER.bronze;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: 14,
        borderRadius: "var(--radius-md)",
        background: t.soft,
        border: "1px solid var(--border-gold)",
      }}
    >
      <span
        style={{
          width: 44,
          height: 44,
          flexShrink: 0,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          background: "var(--surface-card)",
          color: t.color,
        }}
      >
        {iconByName(achievement.iconName, 22)}
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text-primary)" }}>{achievement.name}</div>
        <div className="eyebrow" style={{ fontSize: 10, marginTop: 3 }}>
          {achievement.tier} · unlocked {fmtDate(achievement.unlockedAt)}
        </div>
      </div>
      <div
        className="tnum"
        style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 18, color: "var(--gold-400)", flexShrink: 0 }}
      >
        +{achievement.points}
      </div>
    </div>
  );
}

function ActivityEmbed({ activity }: { activity: NonNullable<SocialPost["activity"]> }) {
  const km = activity.distanceM != null ? (activity.distanceM / 1000).toFixed(1) : null;
  const elev = activity.elevationGainM != null ? Math.round(activity.elevationGainM) : null;
  return (
    <div
      style={{
        borderRadius: "var(--radius-md)",
        border: "1px solid var(--border-subtle)",
        background: "var(--surface-sunken)",
        overflow: "hidden",
      }}
    >
      <ActivityTraceSVG points={activity.tracePoints} height={72} />
      <div style={{ padding: "12px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>{activity.name}</div>
          <div className="eyebrow" style={{ fontSize: 10, marginTop: 3, display: "flex", alignItems: "center", gap: 5 }}>
            <Calendar size={10} />
            {fmtDate(activity.startedAt)}
          </div>
        </div>
        <div className="tnum" style={{ display: "flex", gap: 14, fontSize: 12.5, color: "var(--text-secondary)", flexShrink: 0 }}>
          {km != null && <span>{km} km</span>}
          {elev != null && <span>{elev} m</span>}
          {activity.summitCount > 0 && <span>{activity.summitCount} summit{activity.summitCount === 1 ? "" : "s"}</span>}
        </div>
      </div>
    </div>
  );
}

function PhotoGrid({ photos }: { photos: SocialPost["photos"] }) {
  if (photos.length === 0) return null;

  function open(url: string) {
    window.open(url, "_blank", "noopener,noreferrer");
  }

  const imgStyle = (extra: React.CSSProperties = {}): React.CSSProperties => ({
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "var(--radius-md)",
    cursor: "pointer",
    display: "block",
    ...extra,
  });

  if (photos.length === 1) {
    return (
      <img
        src={photos[0]!.url}
        alt=""
        onClick={() => open(photos[0]!.url)}
        style={{ ...imgStyle(), maxHeight: 360, width: "100%" }}
      />
    );
  }

  if (photos.length === 2) {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, height: 220 }}>
        {photos.map((p, i) => (
          <img key={i} src={p.url} alt="" onClick={() => open(p.url)} style={imgStyle()} />
        ))}
      </div>
    );
  }

  if (photos.length === 3) {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gridTemplateRows: "1fr 1fr", gap: 6, height: 260 }}>
        <img
          src={photos[0]!.url}
          alt=""
          onClick={() => open(photos[0]!.url)}
          style={{ ...imgStyle(), gridRow: "1 / 3" }}
        />
        <img src={photos[1]!.url} alt="" onClick={() => open(photos[1]!.url)} style={imgStyle()} />
        <img src={photos[2]!.url} alt="" onClick={() => open(photos[2]!.url)} style={imgStyle()} />
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr", gap: 6, height: 280 }}>
      {photos.slice(0, 4).map((p, i) => (
        <img key={i} src={p.url} alt="" onClick={() => open(p.url)} style={imgStyle()} />
      ))}
    </div>
  );
}

// ─── Award row ──────────────────────────────────────────────────────────────────

function AwardRow({ awards }: { awards: SocialPost["awards"] }) {
  if (awards.length === 0) return null;
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {awards.map((a) => {
        const earned = a.count > 0;
        return (
          <span
            key={a.itemSlug}
            title={a.name}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "4px 9px",
              borderRadius: "var(--radius-pill)",
              background: earned ? "var(--accent-soft)" : "var(--surface-sunken)",
              border: `1px solid ${earned ? "var(--border-gold)" : "var(--border-subtle)"}`,
              color: earned ? "var(--gold-300)" : "var(--text-faint)",
            }}
          >
            {iconByName(a.icon, 13)}
            <span className="tnum" style={{ fontSize: 12, fontWeight: 700 }}>{a.count}</span>
          </span>
        );
      })}
    </div>
  );
}

// ─── Comments ───────────────────────────────────────────────────────────────────

function CommentRow({
  comment,
  onDelete,
  deleting,
}: {
  comment: PostComment;
  onDelete: () => void;
  deleting: boolean;
}) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
      <ProfileIdentity
        name={comment.author.name ?? comment.author.username ?? "Explorer"}
        avatarUrl={comment.author.avatarUrl}
        size="sm"
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, color: "var(--text-secondary)", lineHeight: 1.45, whiteSpace: "pre-wrap" }}>
          {comment.body}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 3 }}>
          <span className="tnum" style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--text-faint)" }}>
            {formatRelativeTime(comment.createdAt)}
          </span>
          {comment.viewer.canDelete && (
            <button
              type="button"
              onClick={onDelete}
              disabled={deleting}
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 10.5, color: "var(--text-faint)" }}
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CommentsSection({ postId, onCountChange }: { postId: string; onCountChange?: (delta: number) => void }) {
  const queryClient = useQueryClient();
  const [body, setBody] = React.useState("");

  const commentsQuery = useInfiniteQuery({
    queryKey: ["social/posts", postId, "comments"],
    queryFn: ({ pageParam }) => fetchComments(postId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  const items = commentsQuery.data?.pages.flatMap((p) => p.items) ?? [];

  function invalidateAfterChange() {
    void queryClient.invalidateQueries({ queryKey: ["social/posts", postId, "comments"] });
    void queryClient.invalidateQueries({ queryKey: ["social/feed"] });
    void queryClient.invalidateQueries({ queryKey: ["explorers"] });
  }

  const addMutation = useMutation({
    mutationFn: (text: string) => addComment(postId, text),
    onSuccess: () => {
      setBody("");
      onCountChange?.(1);
      invalidateAfterChange();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteComment(id),
    onSuccess: () => {
      onCountChange?.(-1);
      invalidateAfterChange();
    },
  });

  function submit() {
    const trimmed = body.trim();
    if (!trimmed || addMutation.isPending) return;
    addMutation.mutate(trimmed);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 12, borderTop: "1px solid var(--border-subtle)" }}>
      {commentsQuery.isLoading ? (
        <>
          <Skel height="32px" />
          <Skel height="32px" />
        </>
      ) : items.length === 0 ? (
        <p style={{ fontSize: 12.5, color: "var(--text-faint)", margin: 0 }}>No comments yet.</p>
      ) : (
        <>
          {items.map((c) => (
            <CommentRow
              key={c.id}
              comment={c}
              deleting={deleteMutation.isPending && deleteMutation.variables === c.id}
              onDelete={() => deleteMutation.mutate(c.id)}
            />
          ))}
          {commentsQuery.hasNextPage && (
            <button
              type="button"
              onClick={() => void commentsQuery.fetchNextPage()}
              disabled={commentsQuery.isFetchingNextPage}
              style={{
                alignSelf: "flex-start",
                background: "none",
                border: "none",
                padding: 0,
                cursor: commentsQuery.isFetchingNextPage ? "default" : "pointer",
                fontSize: 12,
                fontWeight: 600,
                color: "var(--sky-300)",
              }}
            >
              {commentsQuery.isFetchingNextPage ? "Loading…" : "Load older comments"}
            </button>
          )}
        </>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Add a comment…"
          maxLength={1000}
          style={{
            flex: 1,
            height: "var(--control-sm)",
            padding: "0 12px",
            borderRadius: "var(--radius-pill)",
            border: "1px solid var(--border-default)",
            background: "var(--surface-sunken)",
            color: "var(--text-primary)",
            fontSize: 13,
            fontFamily: "var(--font-sans)",
            outline: "none",
          }}
        />
        <button
          type="button"
          onClick={submit}
          disabled={!body.trim() || addMutation.isPending}
          aria-label="Send comment"
          style={{
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: "50%",
            border: "none",
            display: "grid",
            placeItems: "center",
            background: body.trim() ? "var(--accent)" : "var(--surface-raised)",
            color: body.trim() ? "var(--text-on-gold)" : "var(--text-faint)",
            cursor: body.trim() ? "pointer" : "not-allowed",
          }}
        >
          <Send size={14} />
        </button>
      </div>
      {addMutation.isError && (
        <p style={{ fontSize: 12, color: "var(--status-danger)", margin: 0 }}>
          {errorMessage(addMutation.error, "Failed to comment")}
        </p>
      )}
    </div>
  );
}

// ─── Likers popover ─────────────────────────────────────────────────────────────
// Shown when the author clicks the like control on their own post (self-liking
// is disabled there). Same glass-popover treatment as AwardPicker.

function LikersPopover({ postId, onClose }: { postId: string; onClose: () => void }) {
  const likersQuery = useQuery({
    queryKey: ["social/posts", postId, "likers"],
    queryFn: () => fetchPostLikers(postId),
  });

  const likers = likersQuery.data?.likers ?? [];

  return (
    <div
      role="dialog"
      className="atlas-glass"
      style={{
        position: "absolute",
        bottom: "calc(100% + 8px)",
        left: 0,
        zIndex: 40,
        width: 240,
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--shadow-lg)",
        padding: 14,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <span className="eyebrow" style={{ fontSize: 10 }}>Liked by</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 2 }}
        >
          <X size={14} />
        </button>
      </div>

      {likersQuery.isLoading ? (
        <p style={{ fontSize: 12.5, color: "var(--text-muted)", margin: 0 }}>Loading…</p>
      ) : likersQuery.isError ? (
        <p style={{ fontSize: 12.5, color: "var(--text-muted)", margin: 0 }}>Couldn't load likes.</p>
      ) : likers.length === 0 ? (
        <p style={{ fontSize: 12.5, color: "var(--text-muted)", margin: 0 }}>No likes yet.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 240, overflowY: "auto" }}>
          {likers.map((liker, i) => {
            const identity = (
              <ProfileIdentity
                name={liker.name ?? liker.username ?? "Explorer"}
                avatarUrl={liker.avatarUrl}
                size="sm"
              />
            );
            return liker.username ? (
              <Link
                key={liker.username}
                to="/explorers/$username"
                params={{ username: liker.username }}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                {identity}
              </Link>
            ) : (
              <div key={i}>{identity}</div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── PostCard ───────────────────────────────────────────────────────────────────

export interface PostCardProps {
  post: SocialPost;
  /** Disable interactions for unauthenticated viewers (public profile page). */
  readOnly?: boolean;
}

export function PostCard({ post, readOnly = false }: PostCardProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isOwn = user?.username != null && user.username === post.author.username;
  const [commentsOpen, setCommentsOpen] = React.useState(false);
  const [likersOpen, setLikersOpen] = React.useState(false);
  const [commentCount, setCommentCount] = React.useState(post.commentCount);
  const [liked, setLiked] = React.useState(post.viewer.liked);
  const [likeCount, setLikeCount] = React.useState(post.likeCount);
  const [reportOpen, setReportOpen] = React.useState(false);
  const [reportReason, setReportReason] = React.useState("");
  const [awardPickerOpen, setAwardPickerOpen] = React.useState(false);

  const deleteMutation = useMutation({
    mutationFn: () => deletePost(post.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["social/feed"] });
      void queryClient.invalidateQueries({ queryKey: ["explorers"] });
    },
  });

  // Optimistic toggle; the server response is authoritative and both
  // endpoints are idempotent, so a double-click settles correctly.
  const likeMutation = useMutation({
    mutationFn: (nextLiked: boolean) => (nextLiked ? likePost(post.id) : unlikePost(post.id)),
    onMutate: (nextLiked: boolean) => {
      setLiked(nextLiked);
      setLikeCount((n) => Math.max(0, n + (nextLiked ? 1 : -1)));
    },
    onSuccess: (data) => {
      setLiked(data.liked);
      setLikeCount(data.likeCount);
    },
    onError: (_err, nextLiked) => {
      setLiked(!nextLiked);
      setLikeCount((n) => Math.max(0, n + (nextLiked ? -1 : 1)));
    },
  });

  const reportMutation = useMutation({
    mutationFn: (reason: string) => reportPost(post.id, reason),
    onSuccess: () => {
      setReportReason("");
    },
  });

  function handleDelete() {
    if (window.confirm("Delete this post? This can't be undone.")) {
      deleteMutation.mutate();
    }
  }

  const authorName = post.author.name ?? post.author.username;
  const bannerSrc = getBannerAsset(post.author.banner?.metadata.assetKey);

  return (
    <AtlasPanel style={{ overflow: "visible" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {/* Header — the author's equipped banner renders as a subtle strip
            behind the identity row */}
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 10,
            ...(bannerSrc
              ? {
                  margin: "-8px -8px 0",
                  padding: 8,
                  borderRadius: "var(--radius-md)",
                  overflow: "hidden",
                }
              : {}),
          }}
        >
          {bannerSrc && (
            <>
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  inset: 0,
                  backgroundImage: `url("${bannerSrc}")`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "color-mix(in srgb, var(--surface-card) 62%, transparent)",
                }}
              />
            </>
          )}
          {post.author.profilePublic !== false ? (
            <Link
              to="/explorers/$username"
              params={{ username: post.author.username }}
              style={{ position: "relative", textDecoration: "none", color: "inherit", minWidth: 0 }}
            >
              <ProfileIdentity
                name={authorName}
                avatarUrl={post.author.avatarUrl}
                title={post.author.title}
                badges={post.author.badges}
                size="sm"
              />
            </Link>
          ) : (
            <div style={{ position: "relative", minWidth: 0 }}>
              <ProfileIdentity
                name={authorName}
                avatarUrl={post.author.avatarUrl}
                title={post.author.title}
                badges={post.author.badges}
                size="sm"
              />
            </div>
          )}

          {!readOnly && (
            <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
              <button
                type="button"
                onClick={() => setReportOpen((v) => !v)}
                aria-label="Report post"
                title="Report"
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-faint)", padding: 4 }}
              >
                <TriangleAlert size={14} />
              </button>
              {post.viewer.canDelete && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  aria-label="Delete post"
                  title="Delete"
                  style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-faint)", padding: 4 }}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Body text */}
        {post.body && (
          <p style={{ fontSize: 14.5, color: "var(--text-primary)", lineHeight: 1.55, whiteSpace: "pre-wrap", margin: 0 }}>
            {post.body}
          </p>
        )}

        {/* Embeds */}
        {post.achievement && <AchievementEmbed achievement={post.achievement} />}
        {post.activity && <ActivityEmbed activity={post.activity} />}

        {/* Photos */}
        <PhotoGrid photos={post.photos} />

        {/* Report prompt */}
        {reportOpen && !readOnly && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: 12, borderRadius: "var(--radius-md)", background: "var(--surface-sunken)" }}>
            {reportMutation.isSuccess ? (
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--spruce-300)" }}>
                <Check size={13} /> Reported — thanks for flagging this.
              </div>
            ) : (
              <>
                <textarea
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  placeholder="Why are you reporting this post?"
                  maxLength={500}
                  rows={2}
                  style={{
                    resize: "vertical",
                    padding: 10,
                    borderRadius: "var(--radius-sm)",
                    border: "1px solid var(--border-default)",
                    background: "var(--surface-card)",
                    color: "var(--text-primary)",
                    fontSize: 13,
                    fontFamily: "var(--font-sans)",
                    outline: "none",
                  }}
                />
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <Button variant="ghost" size="sm" onClick={() => setReportOpen(false)}>Cancel</Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!reportReason.trim() || reportMutation.isPending}
                    onClick={() => reportMutation.mutate(reportReason.trim())}
                  >
                    {reportMutation.isPending ? "Sending…" : "Submit report"}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Footer */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            {/* On the author's own post the control lists who liked it instead
                of self-liking */}
            <div style={{ position: "relative" }}>
              <button
                type="button"
                onClick={() => {
                  if (readOnly) return;
                  if (isOwn) setLikersOpen((v) => !v);
                  else likeMutation.mutate(!liked);
                }}
                aria-label={isOwn ? "See who liked this post" : liked ? "Unlike post" : "Like post"}
                aria-pressed={isOwn ? undefined : liked}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  background: "none",
                  border: "none",
                  cursor: readOnly ? "default" : "pointer",
                  color: liked ? "var(--status-danger)" : "var(--text-secondary)",
                  fontSize: 12.5,
                  padding: 0,
                }}
              >
                <Heart size={14} fill={liked ? "currentColor" : "none"} />
                {likeCount > 0 ? `${likeCount} like${likeCount === 1 ? "" : "s"}` : "Like"}
              </button>
              {likersOpen && <LikersPopover postId={post.id} onClose={() => setLikersOpen(false)} />}
            </div>

            <button
              type="button"
              onClick={() => setCommentsOpen((v) => !v)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "var(--text-secondary)",
                fontSize: 12.5,
                padding: 0,
              }}
            >
              <MessageCircle size={14} />
              {commentCount > 0 ? `${commentCount} comment${commentCount === 1 ? "" : "s"}` : "Comment"}
            </button>

            <AwardRow awards={post.awards} />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
            {!readOnly && (
              <div style={{ position: "relative" }}>
                <Button variant="ghost" size="sm" leftIcon={<Gift size={13} />} onClick={() => setAwardPickerOpen((v) => !v)}>
                  Give award
                </Button>
                {awardPickerOpen && (
                  <AwardPicker
                    postId={post.id}
                    onClose={() => setAwardPickerOpen(false)}
                  />
                )}
              </div>
            )}
            {post.visibility === "group" && (
              // Audience marker (spec 32): group feeds mix group-only posts
              // with members' public posts, so say which is which.
              <span
                title="Only this group's members can see this post"
                className="eyebrow"
                style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, color: "var(--text-muted)" }}
              >
                <UsersRound size={11} aria-hidden="true" />
                Group
              </span>
            )}
            <span className="tnum" style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-faint)" }}>
              {formatRelativeTime(post.createdAt)}
            </span>
          </div>
        </div>

        {commentsOpen && (
          <CommentsSection postId={post.id} onCountChange={(delta) => setCommentCount((c) => c + delta)} />
        )}
      </div>
    </AtlasPanel>
  );
}
